import React from 'react';
import { Quote } from 'lucide-react';
import { LearnerExpandableImage } from '../learning/LearnerExpandableImage';
import { normalizeAppHostedUploadUrl } from '../../utils/normalizeAppHostedUploadUrl';

export function hasVocabCollapseMedia(imageUrl?: string | null, example?: string | null): boolean {
  return Boolean(imageUrl?.trim()) || Boolean(example?.trim());
}

interface VocabWordImageProps {
  url: string;
  /** Smaller expand control for thumbnails. */
  compact?: boolean;
  className?: string;
  wrapperClassName?: string;
}

/** Learner vocab image: spinner while loading, then the same expand-to-fullscreen control as book/lesson images. */
export const VocabWordImage: React.FC<VocabWordImageProps> = ({
  url,
  compact = true,
  className,
  wrapperClassName = ''
}) => {
  const src = normalizeAppHostedUploadUrl(url);
  if (!src) return null;

  return (
    <div
      className={`relative inline-block max-w-full shrink-0 overflow-visible ${wrapperClassName}`}
      onClick={(event) => event.stopPropagation()}
      onPointerDown={(event) => event.stopPropagation()}
      onKeyDown={(event) => event.stopPropagation()}
    >
      <LearnerExpandableImage
        src={src}
        alt=""
        compact={compact}
        className={
          className ??
          (compact
            ? 'h-auto max-h-20 w-auto max-w-[9rem] rounded-md object-contain ring-1 ring-black/10 dark:ring-white/10'
            : 'h-auto max-h-40 w-auto max-w-full rounded-md object-contain ring-1 ring-black/10 dark:ring-white/10')
        }
      />
    </div>
  );
};

interface VocabExampleTextProps {
  text: string;
  exampleLabel?: string;
  textClassName?: string;
  dir?: 'rtl' | 'ltr';
}

const VocabExampleText: React.FC<VocabExampleTextProps> = ({
  text,
  exampleLabel,
  textClassName = 'text-xs',
  dir = 'rtl'
}) => (
  <div className="min-w-0 w-full flex-1 text-start" dir={dir}>
    {exampleLabel ? (
      <p className={`mb-1 font-semibold text-gray-500 dark:text-gray-400 ${textClassName}`}>{exampleLabel}</p>
    ) : null}
    <div className="flex gap-1.5">
      {exampleLabel ? null : <Quote className="mt-0.5 h-3 w-3 shrink-0 text-rose-300" aria-hidden />}
      <p dir={dir} className={`min-w-0 whitespace-pre-wrap text-start ${textClassName}`}>
        {text}
      </p>
    </div>
  </div>
);

interface VocabCollapseMediaProps {
  imageUrl?: string | null;
  example?: string | null;
  exampleLabel?: string;
  className?: string;
  exampleTextClassName?: string;
  exampleDir?: 'rtl' | 'ltr';
  /**
   * `left` — image on the physical left of the example (FA vocab grid).
   * `right` — image on the physical right of the example (EN vocab grid).
   * `stack` — image above the example (Leitner due, under the English gloss).
   */
  imagePlacement?: 'left' | 'right' | 'stack';
}

export const VocabCollapseMedia: React.FC<VocabCollapseMediaProps> = ({
  imageUrl,
  example,
  exampleLabel,
  className = '',
  exampleTextClassName = 'text-xs',
  exampleDir = 'rtl',
  imagePlacement = 'left'
}) => {
  const src = normalizeAppHostedUploadUrl(imageUrl);
  const text = example?.trim() ?? '';
  if (!src && !text) return null;

  const image = src ? <VocabWordImage url={src} compact={imagePlacement !== 'stack'} /> : null;
  const exampleNode = text ? (
    <VocabExampleText
      text={text}
      exampleLabel={exampleLabel}
      textClassName={exampleTextClassName}
      dir={exampleDir}
    />
  ) : null;

  if (imagePlacement === 'stack') {
    return (
      <div className={`flex w-full flex-col gap-2 ${className}`}>
        {image ? <div className="flex justify-center">{image}</div> : null}
        {exampleNode}
      </div>
    );
  }

  return (
    <div dir="ltr" className={`flex w-full items-start gap-2 ${className}`}>
      {imagePlacement === 'right' ? (
        <>
          {exampleNode}
          {image}
        </>
      ) : (
        <>
          {image}
          {exampleNode}
        </>
      )}
    </div>
  );
};
